import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Shield, Brain, Clock, TrendingUp, Star, Play, BarChart3, Zap, Award, Lock } from 'lucide-react';

const LandingPage = () => {
  const features = [
    {
      icon: <Brain className="w-8 h-8 text-blue-600" />,
      title: "AI-Powered Bookkeeping",
      description: "Automated transaction categorization and intelligent reconciliation with 99.8% accuracy"
    },
    {
      icon: <BarChart3 className="w-8 h-8 text-green-600" />,
      title: "Smart Insights",
      description: "Real-time financial analytics and predictive insights to optimize your business performance"
    },
    {
      icon: <Shield className="w-8 h-8 text-purple-600" />,
      title: "Compliance Management",
      description: "Automated tax calculations and regulatory compliance with built-in audit trails"
    },
    {
      icon: <Clock className="w-8 h-8 text-orange-600" />,
      title: "Real-Time Reporting",
      description: "Instant financial reports and dashboards that update automatically with every transaction"
    }
  ];

  const benefits = [
    {
      metric: "75%",
      description: "Time saved on bookkeeping tasks"
    },
    {
      metric: "99.8%",
      description: "Accuracy in transaction processing"
    },
    {
      metric: "50+",
      description: "Integrations with banks and apps"
    },
    {
      metric: "24/7",
      description: "Automated compliance monitoring"
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "CFO, TechStart Inc.",
      content: "AccounTech AI transformed our financial operations. We save 15 hours per week on bookkeeping.",
      rating: 5
    },
    {
      name: "Michael Chen",
      role: "Accountant, Growth Partners",
      content: "The AI insights helped us identify cost savings opportunities worth $50K annually.",
      rating: 5
    },
    {
      name: "Emily Rodriguez",
      role: "Small Business Owner",
      content: "Finally, accounting software that actually understands my business needs.",
      rating: 5
    }
  ];

  const pricingPlans = [
    {
      name: "Starter",
      price: "$29",
      period: "/month",
      description: "Perfect for small businesses",
      features: [
        "Up to 500 transactions/month",
        "Basic AI categorization",
        "Standard reports",
        "Email support"
      ],
      popular: false
    },
    {
      name: "Professional",
      price: "$79",
      period: "/month",
      description: "For growing businesses",
      features: [
        "Unlimited transactions",
        "Advanced AI insights",
        "Custom reports",
        "Priority support",
        "Multi-user access",
        "API integrations"
      ],
      popular: true
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "",
      description: "For large organizations",
      features: [
        "Everything in Professional",
        "Dedicated account manager",
        "Custom integrations",
        "Advanced security",
        "On-premise deployment"
      ],
      popular: false
    }
  ];

  const trustSignals = [
    { name: "SOC 2 Compliant", icon: <Shield className="w-6 h-6" /> },
    { name: "256-bit Encryption", icon: <Lock className="w-6 h-6" /> },
    { name: "GDPR Ready", icon: <Award className="w-6 h-6" /> },
    { name: "99.9% Uptime", icon: <Zap className="w-6 h-6" /> }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation Header */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Brain className="w-8 h-8 text-blue-600 mr-2" />
              <span className="text-xl font-bold text-gray-900">AccounTech AI</span>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-600 hover:text-gray-900 transition-colors">Features</a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900 transition-colors">Pricing</a>
              <a href="#resources" className="text-gray-600 hover:text-gray-900 transition-colors">Resources</a>
              <Link to="/login" className="text-gray-600 hover:text-gray-900 transition-colors">Login</Link>
              <Link to="/register" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                Start Free Trial
              </Link>
            </div>
          </div>
        </div>
      </nav>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 leading-tight mb-6">
                AI-Powered Accounting
                <span className="text-blue-600"> Made Simple</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Transform your financial operations with intelligent automation. Save 75% of your time on bookkeeping 
                while achieving 99.8% accuracy in transaction processing.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link 
                  to="/register" 
                  className="bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center justify-center"
                >
                  Start Free Trial
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
                <button className="border border-gray-300 text-gray-700 px-8 py-4 rounded-lg hover:bg-gray-50 transition-colors font-semibold flex items-center justify-center">
                  <Play className="w-5 h-5 mr-2" />
                  Watch Demo
                </button>
              </div>

              <div className="flex items-center text-sm text-gray-500">
                <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                No credit card required • 14-day free trial • Cancel anytime
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl p-8">
                <div className="space-y-4">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-blue-200 rounded w-1/2"></div>
                  <div className="h-4 bg-green-200 rounded w-5/6"></div>
                  <div className="grid grid-cols-2 gap-4 mt-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <TrendingUp className="w-6 h-6 text-blue-600 mb-2" />
                      <div className="text-2xl font-bold text-blue-600">↑ 23%</div>
                      <div className="text-sm text-gray-600">Revenue Growth</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <Clock className="w-6 h-6 text-green-600 mb-2" />
                      <div className="text-2xl font-bold text-green-600">15hrs</div>
                      <div className="text-sm text-gray-600">Time Saved/Week</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Features Showcase */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Powerful Features for Modern Accounting
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to automate your financial operations and make data-driven decisions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features?.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="mb-4">
                  {feature?.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {feature?.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature?.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Trusted by Thousands of Businesses
            </h2>
            <p className="text-xl text-gray-600">
              Join companies that have transformed their financial operations
            </p>
          </div>

          {/* Success Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-16">
            {benefits?.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">
                  {benefit?.metric}
                </div>
                <div className="text-gray-600">
                  {benefit?.description}
                </div>
              </div>
            ))}
          </div>

          {/* Testimonials */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials?.map((testimonial, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-sm">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial?.rating)]?.map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-6 italic">
                  "{testimonial?.content}"
                </p>
                <div>
                  <div className="font-semibold text-gray-900">
                    {testimonial?.name}
                  </div>
                  <div className="text-sm text-gray-500">
                    {testimonial?.role}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Pricing Preview */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600">
              Choose the plan that fits your business needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingPlans?.map((plan, index) => (
              <div key={index} className={`rounded-2xl p-8 ${plan?.popular ? 'bg-blue-50 border-2 border-blue-200 relative' : 'bg-white border border-gray-200'}`}>
                {plan?.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {plan?.name}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {plan?.description}
                  </p>
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-bold text-gray-900">
                      {plan?.price}
                    </span>
                    <span className="text-gray-600 ml-1">
                      {plan?.period}
                    </span>
                  </div>
                </div>

                <ul className="space-y-4 mb-8">
                  {plan?.features?.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
                  plan?.popular 
                    ? 'bg-blue-600 text-white hover:bg-blue-700' :'bg-gray-100 text-gray-900 hover:bg-gray-200'
                }`}>
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Trust Signals */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Enterprise-Grade Security & Compliance
            </h3>
            <p className="text-gray-600">
              Your financial data is protected with industry-leading security measures
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {trustSignals?.map((signal, index) => (
              <div key={index} className="flex flex-col items-center text-center">
                <div className="bg-white rounded-full p-4 mb-4 shadow-sm">
                  {signal?.icon}
                </div>
                <span className="text-gray-700 font-medium">
                  {signal?.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Final CTA */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Accounting?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of businesses that have automated their financial operations with AccounTech AI
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/register"
              className="bg-white text-blue-600 px-8 py-4 rounded-lg hover:bg-gray-50 transition-colors font-semibold flex items-center justify-center"
            >
              Start Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <Link 
              to="/login"
              className="border border-blue-400 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
            >
              Sign In
            </Link>
          </div>
          
          <p className="text-blue-200 text-sm mt-4">
            No credit card required • 14-day free trial • Cancel anytime
          </p>
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <Brain className="w-8 h-8 text-blue-400 mr-2" />
                <span className="text-xl font-bold">AccounTech AI</span>
              </div>
              <p className="text-gray-400">
                AI-powered accounting software that transforms your financial operations.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Integrations</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Webinars</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 AccounTech AI. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;